const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const getHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  const token = localStorage.getItem("token");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.json();
};

export const api = {
  auth: {
    login: async (email, password) => {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      return handleResponse(response);
    },
    logout: async () => {
      const response = await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
    register: async (email, password, full_name, role = "reviewer") => {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, full_name, role }),
      });
      return handleResponse(response);
    },
    me: async () => {
      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
  },

  registrationTypes: {
    list: async () => {
      const response = await fetch(`${API_BASE}/registration-types`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
    create: async (data) => {
      const response = await fetch(`${API_BASE}/registration-types`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },
    update: async (id, data) => {
      const response = await fetch(`${API_BASE}/registration-types/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },
    delete: async (id) => {
      const response = await fetch(`${API_BASE}/registration-types/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
  },

  registrants: {
    list: async (status = null) => {
      const url = status
        ? `${API_BASE}/registrants?status=${status}`
        : `${API_BASE}/registrants`;
      const response = await fetch(url, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
    stats: async () => {
      const response = await fetch(`${API_BASE}/registrants/stats`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
    action: async (id, action) => {
      const response = await fetch(`${API_BASE}/registrants/${id}/action`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ action }),
      });
      return handleResponse(response);
    },
    bulkAction: async (action, registrantIds) => {
      const response = await fetch(`${API_BASE}/registrants/bulk-action`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ action, registrant_ids: registrantIds }),
      });
      return handleResponse(response);
    },
  },

  users: {
    list: async () => {
      const response = await fetch(`${API_BASE}/users`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
    create: async (data) => {
      const response = await fetch(`${API_BASE}/users`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },
    update: async (id, data) => {
      const response = await fetch(`${API_BASE}/users/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },
    delete: async (id) => {
      const response = await fetch(`${API_BASE}/users/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
    resetPassword: async (id, newPassword) => {
      const response = await fetch(`${API_BASE}/users/${id}/reset-password`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ new_password: newPassword }),
      });
      return handleResponse(response);
    },
  },

  dashboard: {
    get: async () => {
      const response = await fetch(`${API_BASE}/admin/dashboard`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
  },

  activityLog: {
    list: async ({ page = 1, pageSize = 50, userId = null, status = null } = {}) => {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (userId) params.append("user_id", userId);
      if (status) params.append("status", status);
      const response = await fetch(`${API_BASE}/admin/activity-log?${params}`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
  },

  settings: {
    getApiConfig: async () => {
      const response = await fetch(`${API_BASE}/eventsair/config`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
    updateApiConfig: async (config) => {
      const response = await fetch(`${API_BASE}/eventsair/config`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(config),
      });
      return handleResponse(response);
    },
    testConnection: async () => {
      const response = await fetch(`${API_BASE}/eventsair/test-connection`, {
        method: "POST",
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
    getActiveEvent: async () => {
      const response = await fetch(`${API_BASE}/eventsair/active-event`, {
        headers: getHeaders(),
      });
      return handleResponse(response);
    },
  },
};
